/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Demo Data Management & Seeding Hub (Task 4.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Trash2,
  PlusCircle,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  Loader2,
  X,
  Sparkles,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';

interface DemoStatusData {
  isDemoActive: boolean;
  demoUsersCount: number;
  demoWorkspacesCount: number;
  demoOrdersCount: number;
  demoSubscriptionsCount: number;
  demoDocumentsCount: number;
  demoLedgerEntriesCount: number;
}

export const DemoDataManagerTab: React.FC = () => {
  const domains = getAdminDomains();

  const [status, setStatus] = useState<DemoStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [purging, setPurging] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/settings/demo-data/status`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (res.ok) {
        const json = (await res.json()) as { success: boolean; data: DemoStatusData };
        if (json.success && json.data) {
          setStatus(json.data);
        }
      }
    } catch {
      // Graceful fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSeed = async () => {
    try {
      setSeeding(true);
      setFeedback(null);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/settings/demo-data/seed`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      const json = (await res.json().catch(() => ({}))) as any;
      if (res.ok && json.success) {
        setFeedback({
          type: 'success',
          message: json.data?.message || 'Gói dữ liệu demo đã được nạp thành công!',
        });
        await fetchStatus();
      } else {
        setFeedback({
          type: 'error',
          message: json.error?.message || 'Không thể nạp dữ liệu demo.',
        });
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err.message || 'Lỗi kết nối khi nạp dữ liệu demo.',
      });
    } finally {
      setSeeding(false);
    }
  };

  const handlePurge = async () => {
    try {
      setPurging(true);
      setFeedback(null);
      setShowPurgeModal(false);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/settings/demo-data/purge`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      const json = (await res.json().catch(() => ({}))) as any;
      if (res.ok && json.success) {
        setFeedback({
          type: 'success',
          message: json.data?.message || 'Đã dọn dẹp sạch toàn bộ dữ liệu demo.',
        });
        await fetchStatus();
      } else {
        setFeedback({
          type: 'error',
          message: json.error?.message || 'Không thể dọn dẹp dữ liệu demo.',
        });
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err.message || 'Lỗi kết nối khi dọn dẹp dữ liệu demo.',
      });
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Status Card */}
      <div className="p-6 rounded-2xl bg-surface border border-surface-border shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-heading font-bold text-foreground">
                Quản lý Môi Trường Dữ Liệu Demo & Thử Nghiệm
              </h3>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                <SophiaIcon name="structure-server" size={11} className="text-primary shrink-0" />
                Two-Tier Seeding
              </span>
            </div>
            <p className="text-xs text-muted-fg max-w-2xl">
              Cơ chế tách biệt độc lập giữa cấu hình hệ thống lõi (Core Seed) và dữ liệu mẫu (Demo Data).
              Giúp triển khai Production hoàn toàn sạch sẽ, đồng thời cho phép kích hoạt dữ liệu demo nhanh chóng khi cần thuyết trình hoặc demo sản phẩm.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchStatus}
            disabled={loading}
            className="p-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted text-muted-fg hover:text-foreground transition-colors shrink-0 disabled:opacity-50"
            title="Làm mới trạng thái"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
          </button>
        </div>

        {/* Status Indicators */}
        <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated/40 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 rounded-xl border ${
                status?.isDemoActive
                  ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                  : 'bg-sky-500/10 text-sky-500 border-sky-500/20'
              }`}
            >
              <SophiaIcon name={status?.isDemoActive ? 'big-data' : 'server'} size={20} />
            </div>

            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-foreground">
                  {status?.isDemoActive
                    ? 'Gói Dữ Liệu Demo Đang Kích Hoạt'
                    : 'Hệ Thống Ở Trạng Thái Khởi Tạo Sạch (Clean State)'}
                </span>
                <span
                  className={`size-2 rounded-full ${
                    status?.isDemoActive ? 'bg-emerald-500 animate-pulse' : 'bg-sky-500'
                  }`}
                />
              </div>
              <p className="text-[11px] text-muted-fg">
                {status?.isDemoActive
                  ? `${status.demoUsersCount} người dùng demo • ${status.demoWorkspacesCount} workspace • ${status.demoOrdersCount} đơn hàng • ${status.demoLedgerEntriesCount} giao dịch credit`
                  : 'Không có bản ghi mang tiền tố demo_ trong cơ sở dữ liệu D1.'}
              </p>
            </div>
          </div>

          <div className="text-right shrink-0">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                status?.isDemoActive
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                  : 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{status?.isDemoActive ? 'Demo Mode Active' : 'Production Clean'}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Feedback Alerts */}
      {feedback && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center gap-2.5 border shadow-sm ${
            feedback.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400'
              : 'bg-destructive/10 border-destructive/20 text-destructive'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Two Action Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card 1: Seed Demo Data */}
        <div className="p-6 rounded-2xl bg-surface border border-surface-border shadow-sm flex flex-col justify-between space-y-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 rounded-xl bg-primary/10 text-primary border border-primary/20">
                <PlusCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-heading font-bold text-foreground">
                  Nạp Gói Dữ Liệu Demo (Seed Demo)
                </h4>
                <span className="text-[10px] text-muted-fg font-mono">
                  tools/seed-demo.ts
                </span>
              </div>
            </div>

            <p className="text-xs text-muted-fg leading-relaxed">
              Tạo lập tự động 5 tài khoản mẫu, 4 không gian làm việc (Innovate AI, Nexus Growth, Quantum Fintech, CloudScale),
              3 gói thuê bao đang hoạt động, 8 đơn hàng giao dịch, và 45 bản ghi biến động credit.
              Thích hợp cho pitching đối tác, demo sản phẩm hoặc kiểm tra UI/UX đầy đủ.
            </p>
          </div>

          <div className="pt-2 border-t border-surface-border flex items-center justify-between">
            <span className="text-[11px] text-muted-fg font-mono">
              Prefix: <code className="text-primary font-bold">demo_*</code>
            </span>
            <button
              type="button"
              onClick={handleSeed}
              disabled={seeding || purging}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover transition-all shadow-sm shadow-primary/20 disabled:opacity-50 cursor-pointer"
            >
              {seeding ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Đang nạp dữ liệu...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Nạp Gói Dữ Liệu Demo</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Card 2: Purge Demo Data */}
        <div className="p-6 rounded-2xl bg-surface border border-surface-border shadow-sm flex flex-col justify-between space-y-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 rounded-xl bg-destructive/10 text-destructive border border-destructive/20">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-heading font-bold text-foreground">
                  Dọn Sạch Dữ Liệu Demo (Purge Demo)
                </h4>
                <span className="text-[10px] text-muted-fg font-mono">
                  tools/purge-demo.ts
                </span>
              </div>
            </div>

            <p className="text-xs text-muted-fg leading-relaxed">
              Quét sạch toàn bộ các thực thể mang tiền tố <code className="text-destructive font-mono">demo_*</code> theo
              thứ tự ràng buộc khóa ngoại (credit ledger &rarr; documents &rarr; orders &rarr; subscriptions &rarr; wallets &rarr; workspaces &rarr; users).
              Hoàn toàn an toàn, tuyệt đối không ảnh hưởng đến tài khoản thật hay cấu hình cốt lõi.
            </p>
          </div>

          <div className="pt-2 border-t border-surface-border flex items-center justify-between">
            <span className="text-[11px] text-muted-fg font-mono">
              An toàn &bull; Đảo ngược khóa ngoại
            </span>
            <button
              type="button"
              onClick={() => setShowPurgeModal(true)}
              disabled={purging || seeding || !status?.isDemoActive}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-destructive/10 text-destructive hover:bg-destructive hover:text-white border border-destructive/20 transition-all disabled:opacity-40 cursor-pointer"
            >
              {purging ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Đang dọn dẹp...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Dọn Sạch Dữ Liệu Demo</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Purge Action */}
      {showPurgeModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl animate-scale-in space-y-5">
            <div className="flex items-center justify-between border-b border-surface-border pb-3">
              <div className="flex items-center gap-2.5 text-destructive">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <h3 className="font-heading font-bold text-sm text-foreground">
                  Xác Nhận Dọn Dẹp Dữ Liệu Mẫu
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPurgeModal(false)}
                className="p-1 rounded-lg text-muted-fg hover:text-foreground hover:bg-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-muted-fg leading-relaxed">
              Bạn có chắc chắn muốn dọn sạch toàn bộ các bản ghi mang tiền tố <code className="font-mono text-destructive">demo_*</code>?
              <br /><br />
              Hệ thống sẽ xóa toàn bộ các tài khoản demo, không gian làm việc demo, đơn hàng và lịch sử credit mẫu.
              Tài khoản Super Admin và các dữ liệu người dùng thật sẽ được bảo vệ nguyên vẹn.
            </p>

            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-[11px] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span>Hành động này an toàn cho dữ liệu thật nhưng không thể hoàn tác.</span>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setShowPurgeModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground hover:bg-muted transition-colors"
              >
                Hủy bỏ
              </button>

              <button
                type="button"
                onClick={handlePurge}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-destructive text-white hover:opacity-90 transition-all shadow-sm"
              >
                Xác nhận dọn dẹp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
